// Libs
import classNames from 'classnames/bind';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { AutoCompleteComponent, BaseInputSearch, BaseSelect, Loading, UnderDevelopment } from '~/components/common';
import { Controller, FormProvider, useFormContext, UseFormReturn } from 'react-hook-form';
// Components, Layouts, Pages
import { TableOrderItem } from '~/components/module/order';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { TypeInventoryGroupDay } from '~/utils/enum/common';
import { IGroupDay, IParamInventoryQuote } from '~/utils/interface/inventory';
import { IBaseOption, IPayloadCallApiFn, IQueryParamBase, IResultCallApiFn } from '~/utils/interface/common';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA,
  EMPTY_STRING,
} from '~/utils/constants/common';
import useDebounce from '~/utils/hooks/useDebounce';
import { ICategory } from '~/utils/interface/category';
import { IFormOrder } from '~/utils/interface/order';
import { IQuoteListItem } from '~/utils/interface/quotes';
import { getListCategory } from '~/thunks/category/categoryThunk';
import { handleDaysBetween } from '~/utils/helper/common';
import { IPaginationResponse } from '~/utils/interface/base';
import { getListInventoryQuote } from '~/thunks/inventory/inventoryThunk';
// Styles, images, icons
import styles from './OrderItemsForm.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const OrderItemsForm = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/order', 'common/base']);
  const { id: orderId } = useParams();
  const dispatch = useAppDispatch();

  const {
    control,
    watch,
    trigger,
    setValue,
    resetField,
    getValues,
    formState: { errors, touchedFields, isSubmitted, isDirty, dirtyFields, defaultValues },
  } = useFormContext<IFormOrder>();

  const { startDate, endDate, orderItemQuantity, orderGroups, selectGroupOrder, orderItems, templateOrderId } = watch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [filterCategory, setFilterCategory] = useState<string[]>([]);
  const debounceFilterCategory = useDebounce(filterCategory, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [searchKey, setSearchKey] = useState<string | null>(null);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [paramObject, setParamObject] = useState<IParamInventoryQuote>({
    page: DEFAULT_CURRENT_PAGE,
    limit: Number(orderItemQuantity) > DEFAULT_LIMIT_PAGE ? Number(orderItemQuantity) : DEFAULT_LIMIT_PAGE,
    orderId: orderId || undefined,
  });

  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [isCheckDetail, setIsCheckDetail] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!startDate || !endDate) return;

    const newParams: IParamInventoryQuote = {
      ...paramObject,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      limit: Number(orderItemQuantity) > DEFAULT_LIMIT_PAGE ? Number(orderItemQuantity) : DEFAULT_LIMIT_PAGE,
    };

    setParamObject(newParams);
  }, [startDate, endDate, orderItemQuantity]);

  useEffect(() => {
    if (!paramObject || !paramObject.startDate || !paramObject.endDate) return;

    handleGetListInventory(paramObject);
  }, [paramObject, templateOrderId]);

  useEffect(() => {
    if (!orderItems || isCheckDetail || !orderId) return;

    const newParams: IParamInventoryQuote = {
      ...paramObject,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      limit: Number(orderItemQuantity) > DEFAULT_LIMIT_PAGE ? Number(orderItemQuantity) : DEFAULT_LIMIT_PAGE,
    };

    orderItems?.length > 0 && setIsCheckDetail(true);

    setParamObject(newParams);
  }, [JSON.stringify(orderItems)]);

  useEffect(() => {
    if (searchKey === null) return;

    setParamObject({
      ...paramObject,
      ...(startDate && endDate && { startDate, endDate }),
      ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);

  useEffect(() => {
    if (debounceFilterCategory === null) return;

    setParamObject({
      ...paramObject,
      ...(startDate && endDate && { startDate, endDate }),
      ...(debounceFilterCategory ? { categoryId: debounceFilterCategory } : { categoryId: undefined }),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceFilterCategory]);

  useEffect(() => {
    if (!orderGroups) return;

    const dataGroupOption: IBaseOption[] =
      orderGroups?.map((item) => ({ value: item.id || EMPTY_STRING, label: item.name })) || [];

    const isFindGroup = dataGroupOption.find((item) => item.value === selectGroupOrder);

    if (isFindGroup) return;

    setValue('selectGroupOrder', EMPTY_STRING);
  }, [JSON.stringify(orderGroups)]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListInventory = (params: IParamInventoryQuote) => {
    setIsLoading(true);

    dispatch(getListInventoryQuote(params))
      .unwrap()
      .then((res) => {
        const { responses: listInventory, pagination } = res?.data;

        if (!listInventory) return;

        const dataInventory: IQuoteListItem[] = listInventory.map((item) => {
          const findQuantity =
            defaultValues?.orderItems?.find(
              (orderItem) =>
                orderItem?.inventoryId === item.id &&
                String(orderItem?.variantId || EMPTY_STRING) === String(item.variantId || EMPTY_STRING)
            )?.quantity || 0;

          const findQuantityAddOns =
            defaultValues?.orderAddOns
              ?.filter(
                (orderItem) =>
                  orderItem?.inventoryId === item.id &&
                  String(orderItem?.variantId || EMPTY_STRING) === String(item.variantId || EMPTY_STRING)
              )
              .map((item) => item?.quantity || 0) || [];

          const findQuantityOverbooking =
            defaultValues?.orderItems?.find(
              (orderItem) =>
                orderItem?.inventoryId === item.id &&
                String(orderItem?.variantId || EMPTY_STRING) === String(item.variantId || EMPTY_STRING)
            )?.overbookedQuantity || 0;

          const findQuantityOverbookingAddOns =
            defaultValues?.orderAddOns
              ?.filter(
                (orderItem) =>
                  orderItem?.inventoryId === item.id &&
                  String(orderItem?.variantId || EMPTY_STRING) === String(item.variantId || EMPTY_STRING)
              )
              .map((item) => item?.overbookedQuantity || 0) || [];

          return {
            ...item,
            quantity: String(findQuantity),
            quantityAddOns: String(findQuantityAddOns?.reduce<number>((sum, item) => sum + (Number(item) || 0), 0)),
            overbookedQuantity: String(findQuantityOverbooking),
            overbookedQuantityAddOns: String(
              findQuantityOverbookingAddOns?.reduce<number>((sum, item) => sum + (Number(item) || 0), 0)
            ),
            quantityInventory:
              item?.groupDayOrder && item?.groupDayOrder?.length > 0
                ? String(
                    Math.min(
                      ...item.groupDayOrder.map((itemOrder) => Number(itemOrder?.availableInventory) + findQuantity)
                    )
                  )
                : String(findQuantity ?? 0),
          };
        });

        const dataListOrderItem = convertDataInventoryGroupDate(dataInventory);

        setValue('listOrderItems', dataListOrderItem);

        setPagination(pagination);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const convertDataInventoryGroupDate = (orderItems: IQuoteListItem[]) => {
    const sortDated = handleDaysBetween(startDate || EMPTY_STRING, endDate || EMPTY_STRING);

    return orderItems.map((item) => {
      const convertQuotes = item?.groupDayQuote?.map((quote) => ({
        ...quote,
        type: TypeInventoryGroupDay.QUOTE,
        availableInventory: (Number(quote?.availableInventory || 0) + Number(item?.quantity || 0)).toString() ?? '0',
      }));

      const convertOrders = item?.groupDayOrder?.map((order) => ({
        ...order,
        type: TypeInventoryGroupDay.ORDER,
        availableInventory:
          (
            Number(order?.availableInventory || 0) +
            Number(item?.quantity || 0) +
            Number(item?.quantityAddOns || 0) -
            Number(item?.overbookedQuantity || 0) -
            Number(item?.overbookedQuantityAddOns || 0)
          ).toString() ?? '0',
      }));

      let mergedDays: IGroupDay[] = [];

      sortDated.forEach((date) => {
        const quote = convertQuotes?.find((quote) => quote?.day === date);
        const order = convertOrders?.find((order) => order?.day === date);

        if (quote && order) {
          mergedDays.push({
            ...order,
            type: TypeInventoryGroupDay.ALL,
          });
        } else if (order) {
          mergedDays.push(order);
        } else if (quote) {
          mergedDays.push(quote);
        } else {
          mergedDays.push({
            day: date,
            quantity: 0,
            availableInventory: '0',
          });
        }
      });

      return { ...item, mergedDays };
    });
  };

  const fetchOptionsCategory = useCallback(
    async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
      const { page, searchKey } = payload;
      const payloadParams: IQueryParamBase = {
        page: page,
        limit: DEFAULT_LIMIT_PAGE,
        ...(searchKey && { textSearch: searchKey }),
      };

      try {
        const resp = await dispatch(getListCategory(payloadParams)).unwrap();

        const { responses: listCategory, pagination } = resp?.data;

        const formatOptions: IBaseOption[] = handleConvertOptionCategory(listCategory);

        const hasMore: boolean = pagination?.page < pagination?.totalPages || false;

        return { optionsResp: formatOptions, hasMoreResp: hasMore };
      } catch (error) {
        return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
      }
    },
    []
  );

  const handleConvertOptionCategory = (data: ICategory[]): IBaseOption[] => {
    const options: IBaseOption[] = data.map((item) => ({
      value: item.id,
      label: item.name,
    }));

    return options;
  };

  const fetchOptionsGroup = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const dataGroupOption: IBaseOption[] =
      orderGroups?.map((item) => ({ value: item.id || EMPTY_STRING, label: item.name })) || [];

    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: dataGroupOption,
      hasMoreResp: false,
    };

    return result;
  }, [JSON.stringify(orderGroups)]);

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
  };
  //#endregion Handle Function

  return (
    <FormProvider
      {...({
        control,
        resetField,
        watch,
        trigger,
        setValue,
        getValues,
        formState: { errors, isDirty, dirtyFields, touchedFields, isSubmitted },
      } as UseFormReturn<IFormOrder>)}
    >
      <Stack className={cx('container')}>
        <Stack className={cx('freeze')}>
          <Stack direction={'row'} spacing={'10px'} alignItems={'center'}>
            <img src={icons.commonIconQuoteItems} alt={t('common_alt_img', { ns: 'common/base' })} />
            <Typography className={cx('title')}>{t('form_orders_title_order_items')}</Typography>
          </Stack>

          <Box className={cx('viewAction')}>
            <Box className={cx('viewSearch')}>
              <BaseInputSearch
                typeStyle='outlined'
                onChange={handleSearchChange}
                borderRadius={100}
                placeholder={t('form_orders_items_title_search_products')}
              />
            </Box>

            <Stack width={546} gap={4} direction={'row'} alignItems={'center'}>
              <AutoCompleteComponent
                multiple={true}
                callApiFn={fetchOptionsCategory}
                placeholder={t('form_orders_items_title_search_category')}
                value={filterCategory}
                width={330}
                onChange={(selected) => {
                  if (Array.isArray(selected)) {
                    setFilterCategory(selected.map((item) => item?.value));
                  }
                }}
              />

              <Controller
                name={`selectGroupOrder`}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <BaseSelect
                    gap={10}
                    height={36}
                    value={value}
                    styleType='extra'
                    placeholder={t('form_orders_items_title_select_group')}
                    callApiFn={fetchOptionsGroup}
                    onChange={(item) => {
                      onChange(item?.value);
                      setValue('selectIdProductOrder', EMPTY_STRING);
                      setValue('selectIdVariantOrder', EMPTY_STRING);
                      setValue('selectGroupItem', EMPTY_STRING);
                      setValue('selectIdProductGroup', EMPTY_STRING);
                      setValue('selectIdVariantGroup', EMPTY_STRING);
                    }}
                  />
                )}
              />
            </Stack>
          </Box>
        </Stack>

        <TableOrderItem
          currentPage={paramObject.page}
          totalPage={pagination?.totalPages}
          onPageChange={handlePageChange}
        />

        {isLoading && <Loading />}

        {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
      </Stack>
    </FormProvider>
  );
};

export default OrderItemsForm;
